import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  AlertCircle,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  History,
  Layers,
  Link2,
  Loader,
  Save,
  Server,
  ShieldCheck,
  StickyNote,
  Trash2,
  PackageCheck,
  BrushCleaning,
  ArrowRightLeft,
  FolderClock,
} from 'lucide-react';
import { modpackConfigsApiService } from '../services/modpackConfigsApi';
import {
  ModpackConfig,
  ModpackConfigEnv,
  ModpackConfigVocabularies,
} from '../types/modpackConfig';
import { canRunModpackMigrations, canRunModpackUpdates, getModpackConfigPerms } from '../utils/modpackConfigPermissions';
import { canAdministerMaintenance } from '../utils/maintenancePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDetailSection } from '../hooks/useDetailSection';
import { useFleetStatus, liveFor } from '../hooks/useFleetStatus';
import type { FleetStatus } from '../hooks/useFleetStatus';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { DetailSections } from '../components/ui/DetailSections';
import { detailSections } from '../config/detailSections';
import { ConfigFieldInput } from '../components/modpackconfigs/ConfigFieldInput';
import { HiddenBadge, LastWipe } from '../components/modpackconfigs/ModpackConfigBadges';
import { LiveStatusBadge } from '../components/modpackconfigs/LiveStatus';
import { LiveStatusPanel } from '../components/modpackconfigs/LiveStatusPanel';
import { ClusterVersionPanel } from '../components/modpackconfigs/ClusterVersionPanel';
import { ProvisionDiscordPanel } from '../components/modpackconfigs/ProvisionDiscordPanel';
import { AnnounceLaunchPanel } from '../components/modpackconfigs/AnnounceLaunchPanel';
import { PackUpdaterPanel } from '../components/modpackconfigs/PackUpdaterPanel';
import { DeepCleanPanel } from '../components/modpackconfigs/DeepCleanPanel';
import { MigrationPanel } from '../components/modpackconfigs/MigrationPanel';
import { PulsarPanel } from '../components/modpackconfigs/PulsarPanel';
import { ResourcePackPanel } from '../components/modpackconfigs/ResourcePackPanel';
import { PackInstallationActivity } from '../components/modpackconfigs/PackInstallationHistory';
import {
  ALL_EDITABLE_FIELDS,
  CLUSTER_FIELDS,
  IDENTITY_FIELDS,
  INDEX_FIELDS,
  NETWORK_FIELDS,
  applyPortConvention,
  isHidden,
  toFormValueFor,
  toPatchValue,
  validateConfigValues,
} from '../components/modpackconfigs/configFields';

type TabId = 'overview' | 'updates' | 'pulsar' | 'resource-packs' | 'deepclean' | 'migration' | 'cluster' | 'integrations' | 'notes' | 'activity';

const TABS = [
  { id: 'overview' as const, label: 'Configuration', icon: Server },
  { id: 'updates' as const, label: 'Update pack', icon: PackageCheck },
  { id: 'pulsar' as const, label: 'Templates & snapshots', icon: FolderClock },
  { id: 'resource-packs' as const, label: 'Resource pack', icon: PackageCheck },
  { id: 'deepclean' as const, label: 'Deep clean', icon: BrushCleaning },
  { id: 'migration' as const, label: 'Migrate server', icon: ArrowRightLeft },
  { id: 'cluster' as const, label: 'Cluster', icon: Layers },
  { id: 'integrations' as const, label: 'Connections', icon: Link2 },
  { id: 'notes' as const, label: 'Pack notes', icon: StickyNote },
  { id: 'activity' as const, label: 'Change history', icon: History },
];

const formFrom = (row: ModpackConfig) =>
  Object.fromEntries(ALL_EDITABLE_FIELDS.map((f) => [String(f.key), toFormValueFor(f, row[f.key])]));

export const ModpackConfigDetailView = () => {
  const { address = '' } = useParams<{ address: string }>();
  const [params] = useSearchParams();
  const env: ModpackConfigEnv = params.get('env') === 'development' ? 'development' : 'production';

  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getModpackConfigPerms(identity, isDev), [identity, isDev]);
  const canMaintain = canAdministerMaintenance(identity);

  const [config, setConfig] = useState<ModpackConfig | null>(null);
  const [siblings, setSiblings] = useState<ModpackConfig[]>([]);
  // Every config, for the cluster-head picker.
  const [allConfigs, setAllConfigs] = useState<ModpackConfig[]>([]);
  const [vocabularies, setVocabularies] = useState<ModpackConfigVocabularies | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedFields, setSavedFields] = useState<string[] | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [hideConfirm, setHideConfirm] = useState(false);
  const [togglingHidden, setTogglingHidden] = useState(false);
  const [copied, setCopied] = useState(false);

  const [activeTab, setActiveTab] = useDetailSection<TabId>(TABS, 'overview');

  // Whether players can reach this server, and whether it is down on purpose:
  // the status page's readings, joined by address.
  const fleet = useFleetStatus({ enabled: perms.canView });
  const live = liveFor(fleet, address);

  useDocumentTitle(config?.modpack_full_name || address || 'Modpack Config');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [row, vocab, all] = await Promise.all([
        modpackConfigsApiService.getConfig(address, env),
        modpackConfigsApiService.getVocabularies(env),
        modpackConfigsApiService.listConfigs(env),
      ]);
      setConfig(row);
      setVocabularies(vocab);
      setAllConfigs(all.results);
      setForm(formFrom(row));

      if (row.cluster_parent !== null) {
        const cluster = await modpackConfigsApiService.getCluster(row.cluster_parent, env);
        setSiblings(cluster.results);
      } else {
        setSiblings([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load this config');
    } finally {
      setLoading(false);
    }
  }, [address, env]);

  useEffect(() => { load(); }, [load]);

  /**
   * Only what actually differs from the loaded row. A whole-row write would
   * clobber the popularity figures Mr. Roboto recalculates every 100 seconds.
   */
  const pendingChanges = useMemo(() => {
    if (!config) return {};
    const patch: Record<string, string | number | null> = {};
    for (const spec of ALL_EDITABLE_FIELDS) {
      if (spec.adminOnly && !perms.canAdmin) continue;
      const { changed, value } = toPatchValue(spec, form[String(spec.key)] ?? '', config[spec.key]);
      if (changed) patch[String(spec.key)] = value;
    }
    return patch;
  }, [config, form, perms.canAdmin]);

  const changedCount = Object.keys(pendingChanges).length;
  const validation = useMemo(() => validateConfigValues(form), [form]);

  const handleSave = async () => {
    if (!config || changedCount === 0) return;
    if (validation.errors.length > 0) {
      setSaveError(validation.errors.map((m) => m.message).join(' '));
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSavedFields(null);
    try {
      const result = await modpackConfigsApiService.updateConfig(
        config.server_address, pendingChanges, env,
      );
      setSavedFields(result.changed);
      await load();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Hiding withdraws the server from everything public — the anonymous API,
   * the LFG list — while staff and the bot keep seeing it. Showing it again is
   * immediate; hiding asks first, because it takes a pack off the public list
   * the moment it is pressed.
   */
  const setHidden = async (hidden: 0 | 1) => {
    if (!config) return;
    setTogglingHidden(true);
    setSaveError(null);
    try {
      await modpackConfigsApiService.updateConfig(config.server_address, { hidden }, env);
      setHideConfirm(false);
      await load();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to change visibility');
      setHideConfirm(false);
    } finally {
      setTogglingHidden(false);
    }
  };

  const handleDelete = async () => {
    if (!config) return;
    setDeleting(true);
    try {
      await modpackConfigsApiService.deleteConfig(config.server_address, env);
      window.location.href = `/modpack-configs${env === 'development' ? '?env=development' : ''}`;
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to delete');
      setDeleting(false);
      setDeleteConfirm(false);
    }
  };

  const handleCopy = () => {
    if (!config) return;
    navigator.clipboard?.writeText(config.server_address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!perms.canView) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Infrastructure" title="Modpack Config" icon={Server} backTo="/modpack-configs" />
        <EmptyState
          tone="warning"
          icon={ShieldCheck}
          title="You don't have permission to view modpack configs"
          description={
            <>Ask an admin for the <code className="font-mono text-xs">argus.modpackconfigs.read</code> permission.</>
          }
        />
      </div>
    );
  }

  if (loading) return <DetailSkeleton />;

  if (error || !config) {
    return (
      <div className="space-y-6">
        <PageHeader eyebrow="Infrastructure" title={address} icon={Server} backTo="/modpack-configs" backLabel="All configs" />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load this config"
          description={error ?? 'No config with that address.'}
          action={
            <div className="flex items-center gap-2">
              <button type="button" onClick={load} className="glass-button">Retry</button>
              <Link to="/modpack-configs" className="glass-button-secondary">Back to all configs</Link>
            </div>
          }
        />
      </div>
    );
  }

  const clusterMembers = siblings.filter((s) => s.id !== config.id);
  const isClusterHead = config.cluster_parent === config.id;
  const successor = clusterMembers.find((m) => m.region_name === 'US') ?? clusterMembers[0] ?? null;
  const hidden = isHidden(config);

  const counts: Partial<Record<TabId, number>> = {
    cluster: siblings.length || undefined,
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Infrastructure"
        title={config.modpack_full_name || config.modpack_name || config.server_address}
        icon={Server}
        backTo={`/modpack-configs${env === 'development' ? '?env=development' : ''}`}
        backLabel="All configs"
        titleAdornment={
          <span className="inline-flex items-center gap-2">
            <LiveStatusBadge live={live} withDetail />
            <HiddenBadge config={config} />
            {env === 'development' && <span className="badge badge-warning">Development</span>}
            {isClusterHead && clusterMembers.length > 0 && (
              <span className="badge badge-purple">Cluster head</span>
            )}
          </span>
        }
        description={<span className="font-mono">{config.server_address}</span>}
        meta={
          <span className="inline-flex items-center gap-2 text-xs text-glass-400 tabular-nums">
            <span>
              id {config.id}
              {config.modpack_version && ` · v${config.modpack_version.replace(/^v/i, '')}`}
            </span>
            <span aria-hidden>·</span>
            <LastWipe config={config} showNever prefix="World wiped" />
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <button type="button" onClick={handleCopy} className="glass-button">
              <Copy className="w-4 h-4" />
              {copied ? 'Copied' : 'Copy address'}
            </button>
            {perms.canWrite && (
              <button
                type="button"
                onClick={() => (hidden ? setHidden(0) : setHideConfirm(true))}
                disabled={togglingHidden}
                className="glass-button"
                title={hidden
                  ? 'Put this server back on the public list and the LFG picker'
                  : 'Withdraw this server from the public API and the LFG picker. Staff and Mr. Roboto still see it.'}
              >
                {togglingHidden ? <Loader className="w-4 h-4 animate-spin" /> : hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                {hidden ? 'Show to players' : 'Hide from players'}
              </button>
            )}
            {perms.canDelete && (
              <button type="button" onClick={() => setDeleteConfirm(true)} className="btn-danger">
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </div>
        }
      />

      {env === 'development' && (
        <div className="alert alert-warning" role="status">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>Development configuration. Changes here do not affect live servers.</span>
        </div>
      )}

      {hidden && (
        <div className="alert alert-warning" role="status">
          <EyeOff className="w-4 h-4 shrink-0" />
          <span>Hidden from public server lists.</span>
        </div>
      )}

      <DetailSections
        sections={detailSections('modpackConfig', TABS, counts)}
        active={activeTab}
        onChange={setActiveTab}
      >
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {saveError && (
              <div className="alert alert-danger" role="alert">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{saveError}</span>
              </div>
            )}
            {savedFields && (
              <div className="alert alert-success" role="status">
                <Save className="w-4 h-4 shrink-0" />
                <span>
                  {savedFields.length === 0
                    ? 'Nothing changed — the values already matched.'
                    : `Saved: ${savedFields.join(', ')}.`}
                </span>
              </div>
            )}

            {!perms.canWrite && (
              <div className="alert alert-info" role="status">
                <ShieldCheck className="w-4 h-4 shrink-0" />
                <span>
                  You can view this config but not change it. Editing needs{' '}
                  <code className="font-mono text-xs">argus.modpackconfigs.write</code>.
                </span>
              </div>
            )}

            <FieldGroup title="Pack" fields={IDENTITY_FIELDS} {...{ form, setForm, vocabularies, perms, validation }} existing={allConfigs} selfId={config.id} />
            <FieldGroup title="Network" fields={NETWORK_FIELDS} {...{ form, setForm, vocabularies, perms, validation }} existing={allConfigs} selfId={config.id} />
            <FieldGroup title="Cluster" fields={CLUSTER_FIELDS} {...{ form, setForm, vocabularies, perms, validation }} existing={allConfigs} selfId={config.id} />
            <FieldGroup title="Catalogue and dates" fields={INDEX_FIELDS} {...{ form, setForm, vocabularies, perms, validation }} existing={allConfigs} selfId={config.id} />

            <div className="subcard p-4">
              <SectionHeader
                title="Popularity"
                description="Recalculated by Mr. Roboto from live rankings every 100 seconds, so these are not editable here."
              />
              <div className="grid grid-cols-3 gap-3">
                {(['popularity_1d', 'popularity_7d', 'popularity_28d'] as const).map((key) => (
                  <div key={key}>
                    <p className="text-xs text-glass-400">{key.replace('popularity_', '')}</p>
                    <p className="text-sm text-white font-mono tabular-nums">
                      {config[key] === null ? '—' : Number(config[key]).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {perms.canWrite && (
              <div className="glass-card p-3 flex items-center justify-between gap-3 sticky bottom-3">
                <p className="text-xs text-glass-400 tabular-nums">
                  {changedCount === 0
                    ? 'No unsaved changes'
                    : `${changedCount} field${changedCount === 1 ? '' : 's'} changed`}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => config && setForm(formFrom(config))}
                    disabled={changedCount === 0 || saving}
                    className="glass-button-secondary"
                  >
                    Discard
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={changedCount === 0 || saving || validation.errors.length > 0}
                    className="btn-primary"
                    title={validation.errors.length > 0 ? validation.errors[0].message : undefined}
                  >
                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save changes
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'updates' && <PackUpdaterPanel config={config} env={env} canWrite={isDev || canRunModpackUpdates(identity)} />}
        {activeTab === 'deepclean' && <DeepCleanPanel config={config} env={env} canWrite={isDev || canRunModpackUpdates(identity)} />}
        {activeTab === 'pulsar' && <PulsarPanel config={config} env={env} canWrite={isDev || canRunModpackUpdates(identity)} initialJobId={params.get('pulsar_job') ?? undefined} />}
        {activeTab === 'resource-packs' && <ResourcePackPanel address={config.server_address} production={env === 'production'} canWrite={isDev || canRunModpackUpdates(identity)} />}
        {activeTab === 'migration' && <MigrationPanel config={config} env={env} canWrite={isDev || canRunModpackMigrations(identity)} />}

        {activeTab === 'cluster' && (
          <ClusterVersionPanel
            config={config}
            members={siblings}
            env={env}
            canWrite={perms.canWrite}
            fleet={fleet}
            onChanged={load}
          />
        )}

        {activeTab === 'integrations' && (
          <IntegrationsPanel
            config={config}
            members={siblings}
            env={env}
            canAdmin={perms.canAdmin}
            canMaintain={canMaintain}
            fleet={fleet}
          />
        )}

        {activeTab === 'notes' && (
          <EmptyState
            icon={StickyNote}
            title="Pack notes live in Modpack Notes"
            description={`Notes are recorded against ${config.server_address} and its cluster, including the note each version bump leaves behind.`}
            action={
              <Link to="/modpack-notes" className="glass-button">
                Open Modpack Notes
                <ExternalLink className="w-4 h-4" />
              </Link>
            }
          />
        )}

        {activeTab === 'activity' && (
          env === 'production' ? <PackInstallationActivity serverAddress={config.server_address} />
            : <EmptyState icon={History} title="Development configuration history"
              description="Development config changes are recorded in the audit log. Live installation history belongs to production server records." />
        )}
      </DetailSections>

      {hideConfirm && (
        <ConfirmationModal
          onClose={() => setHideConfirm(false)}
          onConfirm={() => setHidden(1)}
          isLoading={togglingHidden}
          title={`Hide ${config.server_address} from players?`}
          message={
            'The server leaves the public configs API and the LFG modpack list the moment you confirm. ' +
            'Staff, Argus and Mr. Roboto keep seeing it, and nothing else about it changes — it is not ' +
            'maintenance. Show it again from this page whenever you like.'
          }
          confirmText="Hide from players"
        />
      )}

      {deleteConfirm && (
        <ConfirmationModal
          onClose={() => setDeleteConfirm(false)}
          onConfirm={handleDelete}
          isLoading={deleting}
          title={`Delete ${config.server_address}?`}
          message={
            clusterMembers.length > 0 && isClusterHead
              ? `This server heads a cluster of ${siblings.length}. ${successor?.server_address ?? 'Another member'} will be promoted to head it and the remaining ${clusterMembers.length - 1} will follow it. This cannot be undone.`
              : `This removes the config row${env === 'development' ? ' from servers_dev' : ''}. This cannot be undone.`
          }
          confirmText="Delete config"
        />
      )}
    </div>
  );
};

interface FieldGroupProps {
  title: string;
  fields: typeof IDENTITY_FIELDS;
  form: Record<string, string>;
  setForm: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  vocabularies: ModpackConfigVocabularies | null;
  perms: { canWrite: boolean; canAdmin: boolean };
  validation: ReturnType<typeof validateConfigValues>;
  existing: ModpackConfig[];
  selfId: number;
}

const FieldGroup = ({
  title, fields, form, setForm, vocabularies, perms, validation, existing, selfId,
}: FieldGroupProps) => (
  <div className="subcard p-4">
    <SectionHeader title={title} />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {fields.map((spec) => (
        <ConfigFieldInput
          key={String(spec.key)}
          spec={spec}
          value={form[String(spec.key)] ?? ''}
          // Typing the query port fills in the RCON port by convention.
          onChange={(v) => setForm((prev) => applyPortConvention(prev, String(spec.key), v))}
          vocabularies={vocabularies}
          existing={existing}
          selfId={selfId}
          locked={!perms.canWrite || (spec.adminOnly && !perms.canAdmin)}
          error={validation.errors.find((m) => m.key === spec.key)?.message}
          warning={validation.warnings.find((m) => m.key === spec.key)?.message}
        />
      ))}
    </div>
  </div>
);

/** The other Argus surfaces that know about this server. */
const IntegrationsPanel = ({ config, members, env, canAdmin, canMaintain, fleet }: {
  config: ModpackConfig;
  members: ModpackConfig[];
  env: ModpackConfigEnv;
  canAdmin: boolean;
  canMaintain: boolean;
  fleet: FleetStatus;
}) => (
  <div className="space-y-4">
    <LiveStatusPanel config={config} members={members} env={env} fleet={fleet} canMaintain={canMaintain} />

    {canAdmin && env === 'production' && (
      // Also the recovery path: a launch that stopped after creating the row
      // leaves a pack with no forum or no announcement, and this is where
      // either is finished.
      <>
        <ProvisionDiscordPanel address={config.server_address} env={env} />
        <AnnounceLaunchPanel
          address={config.server_address}
          env={env}
          members={members}
          defaults={{
            location: config.region_name,
            version: config.modpack_version,
            url: config.server_download_url,
          }}
        />
      </>
    )}
    <div className="subcard p-4">
      <SectionHeader title="Pack catalogue" icon={Link2} />
      {config.modpack_index_id ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-glass-300">
            Modpack Index id <span className="font-mono tabular-nums text-white">{config.modpack_index_id}</span>
          </span>
          <Link to={`/modpacks/${config.modpack_index_id}`} className="glass-button-sm">
            Open in Modpack Browser
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <p className="text-xs text-glass-400">
          No Modpack Index id set, so this server is not joined to the pack catalogue and update
          detection will skip it.
        </p>
      )}
    </div>

    <div className="subcard p-4">
      <SectionHeader title="Download" icon={ExternalLink} />
      {config.server_download_url ? (
        <a href={config.server_download_url} target="_blank" rel="noreferrer" className="link break-all text-sm">
          {config.server_download_url}
        </a>
      ) : (
        <p className="text-xs text-glass-400">No download URL set.</p>
      )}
    </div>

    <div className="subcard p-4">
      <SectionHeader title="Restarts" icon={Server} />
      {config.amp_name ? (
        <p className="text-xs text-glass-400">
          Restarts are sent to AMP instance <span className="font-mono text-white">{config.amp_name}</span>.
        </p>
      ) : (
        <p className="text-xs text-glass-400">
          No AMP instance set, so this server cannot be restarted from Argus or Discord.
        </p>
      )}
    </div>
  </div>
);

const DetailSkeleton = () => (
  <div className="space-y-6">
    <div className="space-y-2">
      <SkeletonLoader className="h-4 w-32" />
      <SkeletonLoader className="h-8 w-72" />
      <SkeletonLoader className="h-4 w-56" />
    </div>
    <div className="glass-card p-4 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SkeletonLoader className="h-16" />
          <SkeletonLoader className="h-16" />
        </div>
      ))}
    </div>
  </div>
);
